/**
 * Import Review Service
 *
 * Backs the admin-only import-review screen: reads recipes a collection import
 * left in `pendingReview`, and moves them through approved / rejected /
 * published.
 *
 * Status changes are written straight to Firestore — the recipes live in the
 * cook's own scope (their household's, when they have one) and firestore.rules
 * already permits it, so no API round trip is needed. Publishing is the exception: `/recipes` is
 * admin-SDK-write-only, so it goes through the server where the admin
 * allowlist is actually enforced.
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  updateDoc,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { Recipe, RecipeReviewStatus } from '../types/recipe';
import { getAuthHeaders } from './apiClient';
import { getApiBaseUrl } from '../config/apiBase';
import unifiedRecipeService from './unifiedRecipeService';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import { invalidateRecipeIndex } from '../redux/recipeIndexSlice';

/** One roundup's worth of imported recipes. */
export interface ImportBatch {
  batchId: string;
  title: string;
  sourceUrl?: string;
  importedAt?: string;
  recipes: Recipe[];
}

export interface PublishResult {
  published: { id: string; catalogId: string; title: string }[];
  skipped: { id: string; reason: string }[];
}

/**
 * The saved-recipes collection batch imports land in: the household's when the
 * cook has one, else their own. It must match where api/recipes/import.ts
 * writes (resolveSavedRecipesCollection), or the queue reads the wrong one.
 *
 * `householdId` is passed in from `state.auth.householdId` rather than read
 * from householdContext: that cache is only filled once the profile loads, so a
 * screen opened straight after a reload counted the personal path instead.
 */
function savedRecipesRef(userId: string, householdId: string | null) {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'savedRecipes');
  return collection(firestore, root, id, ...rest);
}

/** Recipes still awaiting a decision. */
export async function fetchPendingReview(
  userId: string,
  householdId: string | null
): Promise<Recipe[]> {
  if (!isFirebaseEnabled || !userId) return [];

  const snapshot = await getDocs(
    query(savedRecipesRef(userId, householdId), where('reviewStatus', 'in', ['pendingReview', 'approved']))
  );

  return snapshot.docs.map((d) => ({ ...(d.data() as Recipe), id: d.id }));
}

/** Just the count, for the badge on the recipes screen. */
export async function fetchPendingReviewCount(
  userId: string,
  householdId: string | null
): Promise<number> {
  if (!isFirebaseEnabled || !userId) return 0;

  const snapshot = await getDocs(
    query(savedRecipesRef(userId, householdId), where('reviewStatus', '==', 'pendingReview'))
  );
  return snapshot.size;
}

/**
 * Group pending recipes by the roundup they came from.
 * Batch metadata is denormalized onto each recipe, so this needs no extra read.
 */
export function groupByBatch(recipes: Recipe[]): ImportBatch[] {
  const batches = new Map<string, ImportBatch>();

  for (const recipe of recipes) {
    const batchId = recipe.importBatchId || 'ungrouped';
    let batch = batches.get(batchId);
    if (!batch) {
      batch = {
        batchId,
        title: recipe.importBatchTitle || 'Imported recipes',
        sourceUrl: recipe.importBatchSourceUrl,
        // `savedAt` is written by the import endpoint but isn't part of the
        // Recipe type (it belongs to the stored document shape).
        importedAt: (recipe as Recipe & { savedAt?: string }).savedAt,
        recipes: [],
      };
      batches.set(batchId, batch);
    }
    batch.recipes.push(recipe);
  }

  // Newest batch first.
  return Array.from(batches.values()).sort((a, b) =>
    (b.importedAt || '').localeCompare(a.importedAt || '')
  );
}

/** Approve or reject a single recipe. */
export async function setReviewStatus(
  userId: string,
  householdId: string | null,
  recipeId: string,
  status: RecipeReviewStatus
): Promise<void> {
  await updateDoc(doc(savedRecipesRef(userId, householdId), recipeId), { reviewStatus: status });
}

/**
 * Promote recipes into the public catalog.
 *
 * Server-side: the admin allowlist, the content re-check and the catalog
 * dedupe all live there, so a partial success is normal and comes back in
 * `skipped` rather than as an error.
 */
export async function publishRecipes(recipeIds: string[]): Promise<PublishResult> {
  const apiBaseUrl = getApiBaseUrl();
  const headers = await getAuthHeaders();

  const response = await fetch(`${apiBaseUrl}/api/recipes/import`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action: 'publish', recipeIds }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || `Publish failed (HTTP ${response.status})`);
  }

  return { published: data.published || [], skipped: data.skipped || [] };
}

/**
 * Make a publish visible in the library on its next focus.
 *
 * The library serves the persisted recipe index until it is 30 minutes old,
 * and unifiedRecipeService keeps its own 5-minute copy, so a new `/recipes`
 * document is invisible until both lapse.
 */
export function refreshLibraryAfterPublish(
  result: PublishResult,
  dispatch: (action: ReturnType<typeof invalidateRecipeIndex>) => unknown
): void {
  if (result.published.length === 0) return;
  unifiedRecipeService.invalidateCache();
  dispatch(invalidateRecipeIndex());
}
